import React, {useEffect, useRef, useState} from "react";
import "../DesignerRenderer.css";
import {getLiveTagByName, getLiveTagValueByName} from "../utils";

const TagRenderer = ({
                         object,
                         latestTags,
                         styleJson,
                         dataJson,
                         commonStyle,
                         handleMouseDown,
                         onButtonAction,
                         renderSelectionHandles,
                     }) => {
    const initializedAnalogRef = useRef(false);
    const lastLiveValueRef = useRef({key: "", value: ""});
    const [analogInputValue, setAnalogInputValue] = useState("");
    const [isAnalogDirty, setIsAnalogDirty] = useState(false);

    const tagMode = dataJson.tag_mode || "manual";
    const objectMode = dataJson.object_mode || "monitor";
    const controlType = dataJson.control_type || "digital";
    const realtimeTagName = dataJson.tag_name || "";

    const prefix = dataJson.prefix || "";
    const suffix = dataJson.suffix || "";
    const unit = dataJson.unit || "";

    const cacheKey = `${object.id ?? "object"}:${realtimeTagName}`;
    if (lastLiveValueRef.current.key !== cacheKey) {
        lastLiveValueRef.current = {key: cacheKey, value: ""};
    }

    const liveValue = getLiveTagValueByName(latestTags, realtimeTagName);
    const liveTag = getLiveTagByName(latestTags, realtimeTagName);
    if (tagMode === "realtime" && liveValue !== "") {
        lastLiveValueRef.current.value = liveValue;
    }

    const lastLiveValue = lastLiveValueRef.current.value;
    const hasRealtimeBinding = tagMode === "realtime" && Boolean(realtimeTagName);
    const connectionStatus = hasRealtimeBinding
        ? String(liveTag?.connection_status || "UNKNOWN").toUpperCase()
        : "";
    const isUnavailable = hasRealtimeBinding && (
        !liveTag || ["OFFLINE", "UNKNOWN"].includes(connectionStatus)
    );
    const isOff = tagMode === "off";
    const usesWarningAppearance = isOff || isUnavailable;
    const liveSignalType = String(liveTag?.signal_type || "").toUpperCase();
    const resolvedControlType = liveSignalType === "ANALOG"
        ? "analog"
        : liveSignalType === "DIGITAL"
            ? "digital"
            : controlType;
    const effectiveLiveValue = liveValue !== "" ? liveValue : lastLiveValue;

    useEffect(() => {
        initializedAnalogRef.current = false;
        setIsAnalogDirty(false);
        setAnalogInputValue("");
    }, [object.id, dataJson.tag_name, dataJson.control_type, dataJson.object_mode]);

    useEffect(() => {
        if (objectMode !== "control" || resolvedControlType !== "analog" || usesWarningAppearance) return;
        if (initializedAnalogRef.current) return;

        setAnalogInputValue(effectiveLiveValue);
        initializedAnalogRef.current = true;
    }, [objectMode, resolvedControlType, realtimeTagName, effectiveLiveValue, usesWarningAppearance]);

    let resolvedValue = dataJson.value ?? "N/A";

    if (tagMode === "realtime") {
        resolvedValue = effectiveLiveValue !== "" ? effectiveLiveValue : "N/A";
    }

    const selectedHandles = renderSelectionHandles();

    const handleLocalMouseDown = (e) => {
        e.stopPropagation();
        handleMouseDown?.(e);
    };

    return (
        <div
            onClick={(e) => e.stopPropagation()}
            onMouseDown={handleLocalMouseDown}
            className={`dr-object dr-tag${usesWarningAppearance ? " dr-tag--offline" : ""}`}
            title={isOff
                ? "Tag display is off"
                : isUnavailable
                    ? `${realtimeTagName || "Tag"} is ${connectionStatus.toLowerCase()}`
                    : undefined}
            data-tag-mode={tagMode}
            data-tag-status={connectionStatus || undefined}
            data-tag-signal-type={liveSignalType || undefined}
            style={{
                position: "absolute",
                ...commonStyle,
                fontSize: styleJson.fontSize || 16,
                fontWeight: styleJson.fontWeight || "normal",
                fontFamily: styleJson.fontFamily || "Arial",

                // 👇 کنترل بکگراند
                background: usesWarningAppearance
                    ? "#d7bdc3"
                    : styleJson.hasBackground
                        ? styleJson.backgroundColor || "#e0f2fe"
                        : "transparent",
                color: commonStyle.color,
                border: usesWarningAppearance ? "1px solid #9b6873" : commonStyle.border,

                borderRadius: styleJson.borderRadius || 0,
                padding: usesWarningAppearance || styleJson.hasBackground ? styleJson.padding || 4 : 0,

                outline: selectedHandles ? "2px solid #2563eb" : "none",
                outlineOffset: selectedHandles ? "2px" : "0",
            }}
        >
            <div className="dr-tag-content">
                {isOff ? prefix : (
                    <>
                        {prefix}
                        {String(resolvedValue)}
                        {unit}
                        {suffix}
                    </>
                )}
            </div>

            {objectMode === "control" && !object.locked && !usesWarningAppearance && (
                <div
                    className="dr-control-group"
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                >
                    {resolvedControlType === "digital" ? (
                        <>
                            <button
                                type="button"
                                className="dr-control-btn dr-control-btn--green"
                                onClick={() =>
                                    typeof onButtonAction === "function" &&
                                    onButtonAction({
                                        ...object,
                                        __controlType: "digital",
                                        __controlValue: 1,
                                    })
                                }
                            >
                                ON
                            </button>

                            <button
                                type="button"
                                className="dr-control-btn dr-control-btn--red"
                                onClick={() =>
                                    typeof onButtonAction === "function" &&
                                    onButtonAction({
                                        ...object,
                                        __controlType: "digital",
                                        __controlValue: 0,
                                    })
                                }
                            >
                                OFF
                            </button>
                        </>
                    ) : (
                        <>
                            <input
                                type="number"
                                step="any"
                                value={analogInputValue}
                                onChange={(e) => {
                                    setAnalogInputValue(e.target.value);
                                    setIsAnalogDirty(true);
                                }}
                                placeholder={effectiveLiveValue || "live value"}
                                className="dr-control-input"
                            />

                            <button
                                type="button"
                                className="dr-control-btn dr-control-btn--blue"
                                onClick={() => {
                                    const finalValue =
                                        analogInputValue === "" ? effectiveLiveValue : analogInputValue;

                                    if (typeof onButtonAction === "function") {
                                        onButtonAction({
                                            ...object,
                                            __controlType: "analog",
                                            __controlValue: finalValue,
                                        });
                                    }
                                }}
                            >
                                Write
                            </button>

                            {!isAnalogDirty && effectiveLiveValue && (
                                <span className="dr-live-badge">live</span>
                            )}
                        </>
                    )}
                </div>
            )}

            {selectedHandles}
        </div>
    );
};

export default TagRenderer;
